import type { LoyaltyBonusAktion, LoyaltyEinstellung, LoyaltyVorteil, Me, ModulFreischaltung } from '@portal/contracts';

/**
 * „House of Brückner - Royalty“ (#856 Phase 2a): reine Hilfsfunktionen der Team-Seite (Routen, Anzeige, Rechte) — getestet
 * ohne DOM. Rechte prüft immer die API; die Oberfläche blendet nur aus, was ohnehin abgelehnt würde.
 */
export const LOYALTY_PFAD = '/loyalty';

export type LoyaltyRoute =
  | { seite: 'uebersicht' }
  | { seite: 'konten' }
  | { seite: 'konto'; id: string }
  | { seite: 'regelwerk' }
  | { seite: 'unbekannt' };

export const UNTERNAVIGATION = [
  { seite: 'uebersicht', label: 'Übersicht', pfad: LOYALTY_PFAD },
  { seite: 'konten', label: 'Konten', pfad: `${LOYALTY_PFAD}/konten` },
  { seite: 'regelwerk', label: 'Regelwerk', pfad: `${LOYALTY_PFAD}/regelwerk` },
] as const;

export function loyaltyRoute(pathname: string): LoyaltyRoute {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === LOYALTY_PFAD) return { seite: 'uebersicht' };
  if (p === `${LOYALTY_PFAD}/konten`) return { seite: 'konten' };
  if (p === `${LOYALTY_PFAD}/regelwerk`) return { seite: 'regelwerk' };
  const m = /^\/loyalty\/konten\/([0-9a-f-]{36})$/i.exec(p);
  if (m) return { seite: 'konto', id: m[1]! };
  return { seite: 'unbekannt' };
}

/** aktiver Reiter der Unternavigation (Kontodetail gehört zu „Konten“) */
export function aktiverReiter(r: LoyaltyRoute): 'uebersicht' | 'konten' | 'regelwerk' | null {
  if (r.seite === 'konto') return 'konten';
  return r.seite === 'unbekannt' ? null : r.seite;
}

export const kontoPfad = (id: string) => `${LOYALTY_PFAD}/konten/${encodeURIComponent(id)}`;

/** Stufe immer mit Ziffer und Name (Briefing § 1): „3 · Noble“ */
export function stufeAnzeige(nr: number, name: string | null): string {
  return name ? `${nr} · ${name}` : String(nr);
}

type MitModulen = Pick<Me, 'modules'> | { modules?: ModulFreischaltung };
export const darfVerwalten = (me: MitModulen): boolean => me.modules?.loyalty === 'verwalten';

const ART: Record<LoyaltyVorteil['art'], string> = {
  freischaltung: 'Freischaltung', gutschein: 'Gutschein', geschenk: 'Geschenk', beigabe: 'Beigabe',
  zaehler: 'Zähler', event: 'Event', service: 'Service', info: 'Info',
};
const ZEITRAUM: Record<string, string> = { statusjahr: 'pro Statusjahr', bestellung: 'je Bestellung', einmalig: 'einmalig', aufstieg: 'beim Aufstieg' };

export const artAnzeige = (a: LoyaltyVorteil['art']) => ART[a] ?? a;

/** „2× pro Statusjahr“, „einmalig“ … ; ohne Menge leer */
export function haeufigkeit(v: Pick<LoyaltyVorteil, 'mengeJeZeitraum' | 'zeitraum'>): string {
  if (!v.zeitraum) return '';
  const z = ZEITRAUM[v.zeitraum] ?? v.zeitraum;
  return v.mengeJeZeitraum && v.zeitraum !== 'einmalig' && v.zeitraum !== 'aufstieg' ? `${v.mengeJeZeitraum}× ${z}` : z;
}

/** Vorteile nach Stufe gruppiert, innerhalb nach Briefing-Nr. */
export function vorteileJeStufe(vorteile: readonly LoyaltyVorteil[]): Map<number, LoyaltyVorteil[]> {
  const out = new Map<number, LoyaltyVorteil[]>();
  for (const v of [...vorteile].sort((a, b) => a.code.localeCompare(b.code, 'de', { numeric: true }))) {
    out.set(v.stufeAb, [...(out.get(v.stufeAb) ?? []), v]);
  }
  return out;
}

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
export const schwelleAnzeige = (eur: number | null) => (eur === null ? 'nur auf Einladung' : `ab ${EUR.format(eur)}`);
export const crownsAnzeige = (c: number) => `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(c)} je €`;

/** Einstellungswert lesbar (JSON → Text) */
export function wertAnzeige(e: Pick<LoyaltyEinstellung, 'wert' | 'einheit'>): string {
  if (e.wert === null || e.wert === undefined) return '— (noch offen)';
  const w = typeof e.wert === 'number' ? new Intl.NumberFormat('de-DE').format(e.wert) : typeof e.wert === 'string' ? e.wert.replace(/_/g, ' ') : JSON.stringify(e.wert);
  return e.einheit ? `${w} ${e.einheit}` : w;
}

/** Bonus-Aktion lesbar: „150 Crowns; +50 jährlich“, „500 bzw. 250 Crowns, max. 10 je Statusjahr – Hinweis“ */
export function bonusAnzeige(b: Pick<LoyaltyBonusAktion, 'crowns' | 'crownsZusatz' | 'crownsJaehrlich' | 'deckel' | 'deckelZeitraum' | 'hinweis'>): string {
  const teile = [`${b.crowns}${b.crownsZusatz !== null ? ` bzw. ${b.crownsZusatz}` : ''} Crowns`];
  if (b.crownsJaehrlich) teile[0] += `; +${b.crownsJaehrlich} jährlich`;
  if (b.deckel) teile.push(`max. ${b.deckel} je ${b.deckelZeitraum === 'monat' ? 'Monat' : 'Statusjahr'}`);
  return teile.join(', ') + (b.hinweis ? ` – ${b.hinweis}` : '');
}

/** Audit-Akteur „Anzeigename (principal-id)“ → Anzeigename (wie CRM-Protokoll) */
export const akteurName = (von: string): string => /^(.*\S)\s\(([^()]+)\)$/.exec(von)?.[1] ?? von;
